import { useState } from "react";

const seats = [
  {
    tier: "Bar Rail",
    price: "Free",
    perGame: "Walk-in Welcome",
    color: "from-zinc-800 to-zinc-900",
    badge: null,
    icon: "ri-goblet-line",
    description: "First-come, first-served energy. Grab a spot at the bar and get right in the action.",
    features: [
      "Open bar rail seating",
      "Closest to the screens",
      "Walk-in, no reservation needed",
      "Full drink & food menu available",
      "The Full 90 deal eligible",
    ],
    cta: "Walk In",
    ctaStyle: "bg-white text-black",
    ctaHref: "#full90",
    isUpgrade: false,
  },
  {
    tier: "Pick Your Spot",
    price: "$20",
    perGame: "Per Person",
    color: "from-[#c9a84c] to-[#e8c34a]",
    badge: "Most Popular",
    icon: "ri-map-pin-2-line",
    description: "Choose exactly where you want to be. Select your preferred zone — front row, center floor, side lounge — your call.",
    features: [
      "Choose your exact seating zone",
      "Guaranteed spot in chosen area",
      "Minimum guests required per zone",
      "Dedicated server all game",
      "The Full 90 deal eligible",
      "Book via Resy",
    ],
    cta: "Upgrade My Seat",
    ctaStyle: "bg-black text-white",
    ctaHref: "mailto:clarice@n17thelane.com?subject=Seat%20Upgrade%20Request&body=I%20want%20to%20upgrade%20and%20choose%20my%20seat%20for%20%2420%20per%20seat",
    isUpgrade: true,
  },
  {
    tier: "Group Buyout",
    price: "Inquire",
    perGame: "For Large Groups (15+)",
    color: "from-zinc-800 to-zinc-900",
    badge: "Groups",
    icon: "ri-group-line",
    description: "Take over our private back room for your squad. Perfect for corporate watch parties and big groups.",
    features: [
      "Private back room exclusive access",
      "Dedicated section server",
      "Custom group Full 90 packages",
      "Pre-ordered food & drinks",
      "AV setup included",
    ],
    cta: "Contact Us",
    ctaStyle: "bg-white text-black",
    ctaHref: "mailto:info@n17thelane.com",
    isUpgrade: false,
  },
];

const zones = [
  {
    id: "high-tops",
    name: "High Tops",
    icon: "ri-table-line",
    description: "Standing-height tables with bar stools — right in the energy of the room with great screen angles all around.",
    minGuests: 2,
    maxGuests: 6,
    image: "https://storage.readdy-site.link/project_files/ae39c9fb-c0ef-4d19-8b6a-d8adfd2c0a75/f59b185c-28e6-4c7b-a3a7-4032f6ec5dbe_Screenshot-2024-09-18-at-10.49.37PM.png?v=ccae6cb9d5e5924d2387224dcf16c745",
  },
  {
    id: "lounge",
    name: "Lounge",
    icon: "ri-sofa-line",
    description: "Comfortable lounge seating with low tables — a relaxed way to catch every minute of the match.",
    minGuests: 2,
    maxGuests: 8,
    image: "https://storage.readdy-site.link/project_files/ae39c9fb-c0ef-4d19-8b6a-d8adfd2c0a75/fb6f679d-620a-45c3-8ba0-96555211c9e9_Screenshot-2024-11-18-at-4.37.45PM.jpg?v=f51de540e5008d3f7e876d0c1024b467",
  },
];

export default function TicketOptions() {
  const [activeZone, setActiveZone] = useState(zones[0].id);
  const selectedZone = zones.find((z) => z.id === activeZone)!;

  return (
    <section id="tickets" className="bg-[#f7f4ee] py-24 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-4">
          <div>
            <span className="inline-block bg-black text-[#e8c34a] text-xs uppercase tracking-[0.3em] font-bold px-4 py-1.5 rounded-full mb-4">
              Seating
            </span>
            <h2 className="text-black font-black text-4xl md:text-6xl uppercase leading-none">
              Choose
              <br />
              <span className="text-[#e8c34a] bg-black px-2">Your Seat</span>
            </h2>
          </div>
          <p className="text-black/50 text-sm max-w-sm leading-relaxed">
            Walk in free or pay $20/person to lock in exactly where you want to be. Pick your zone, meet the minimum, and own your spot for the full 90.
          </p>
        </div>

        {/* Seat packages */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-16">
          {seats.map((seat) => (
            <div
              key={seat.tier}
              className={`relative rounded-2xl bg-gradient-to-b ${seat.color} p-6 flex flex-col`}
            >
              {seat.badge && (
                <span className="absolute top-4 right-4 bg-[#e8c34a] text-black text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                  {seat.badge}
                </span>
              )}

              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/10 mb-4">
                <i className={`${seat.icon} text-[#e8c34a] text-xl`}></i>
              </div>

              <p className="text-white/50 text-xs uppercase tracking-[0.25em] font-medium mb-1">
                {seat.tier}
              </p>
              <p className="text-white font-black text-2xl mb-1 leading-tight">{seat.price}</p>
              <p className="text-white/40 text-xs mb-4">{seat.perGame}</p>
              <p className="text-white/60 text-xs leading-relaxed mb-5">{seat.description}</p>

              <ul className="flex flex-col gap-2.5 flex-1 mb-6">
                {seat.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-white/70 text-sm">
                    <i className="ri-check-line text-[#e8c34a] text-sm mt-0.5 flex-shrink-0"></i>
                    {f}
                  </li>
                ))}
              </ul>

              {seat.isUpgrade && (
                <div className="mb-4 bg-white/10 rounded-xl px-4 py-3 flex items-center gap-2">
                  <i className="ri-information-line text-[#e8c34a] text-sm flex-shrink-0"></i>
                  <p className="text-white/70 text-xs leading-snug">
                    Explore available zones below. Minimum guest count required per zone.
                  </p>
                </div>
              )}

              <a
                href={seat.ctaHref}
                target={seat.ctaHref.startsWith("http") ? "_blank" : undefined}
                rel={seat.ctaHref.startsWith("http") ? "noopener noreferrer" : undefined}
                className={`flex items-center justify-center gap-2 ${seat.ctaStyle} font-bold px-5 py-3 rounded-xl uppercase tracking-wider text-sm hover:opacity-90 transition-all cursor-pointer whitespace-nowrap`}
              >
                {seat.cta}
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          ))}
        </div>

        {/* Zone Picker */}
        <div className="bg-black rounded-3xl overflow-hidden">
          <div className="px-8 pt-8 pb-0">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
              <div>
                <span className="inline-block bg-[#e8c34a]/20 text-[#e8c34a] text-xs uppercase tracking-[0.3em] font-bold px-3 py-1 rounded-full mb-3">
                  $20 / person upgrade
                </span>
                <h3 className="text-white font-black text-2xl md:text-3xl uppercase">
                  Pick Your Zone
                </h3>
                <p className="text-white/40 text-sm mt-1">
                  Select a zone to see details. Minimum guests apply.
                </p>
              </div>
              <a
                href="mailto:clarice@n17thelane.com?subject=Seat%20Upgrade%20Request&body=I%20want%20to%20upgrade%20and%20choose%20my%20seat%20for%20%2420%20per%20seat"
                className="inline-flex items-center gap-2 bg-[#e8c34a] text-black font-bold px-6 py-3 rounded-full uppercase tracking-wider text-sm hover:bg-[#f0d060] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-send-line"></i>
                Upgrade My Seat
              </a>
            </div>

            {/* Zone tabs */}
            <div className="flex gap-2 flex-wrap">
              {zones.map((zone) => (
                <button
                  key={zone.id}
                  onClick={() => setActiveZone(zone.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-t-xl text-sm font-semibold uppercase tracking-wide transition-all cursor-pointer whitespace-nowrap ${
                    activeZone === zone.id
                      ? "bg-[#f7f4ee] text-black"
                      : "bg-white/10 text-white/60 hover:bg-white/20 hover:text-white"
                  }`}
                >
                  <i className={`${zone.icon} text-base`}></i>
                  {zone.name}
                </button>
              ))}
            </div>
          </div>

          {/* Zone detail */}
          <div className="bg-[#f7f4ee]">
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="relative h-64 md:h-auto min-h-[260px]">
                <img
                  src={selectedZone.image}
                  alt={selectedZone.name}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent"></div>
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-black">
                    <i className={`${selectedZone.icon} text-[#e8c34a] text-xl`}></i>
                  </div>
                  <h4 className="text-black font-black text-2xl uppercase">{selectedZone.name}</h4>
                </div>
                <p className="text-black/60 text-sm mb-6 leading-relaxed">{selectedZone.description}</p>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-black rounded-xl p-4">
                    <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Min Guests</p>
                    <p className="text-white font-black text-2xl">{selectedZone.minGuests}</p>
                  </div>
                  <div className="bg-black rounded-xl p-4">
                    <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Max Guests</p>
                    <p className="text-white font-black text-2xl">{selectedZone.maxGuests}</p>
                  </div>
                  <div className="bg-black rounded-xl p-4 col-span-2">
                    <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Upgrade Cost</p>
                    <p className="text-white font-black text-2xl">$20 <span className="text-white/40 text-sm font-normal">per person</span></p>
                  </div>
                </div>

                <a
                  href={`mailto:clarice@n17thelane.com?subject=Seat%20Upgrade%20Request%20%E2%80%94%20${encodeURIComponent(selectedZone.name)}&body=I%20want%20to%20upgrade%20and%20choose%20my%20seat%20for%20%2420%20per%20seat`}
                  className="inline-flex items-center justify-center gap-2 bg-black text-white font-bold px-6 py-3 rounded-xl uppercase tracking-wider text-sm hover:bg-zinc-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-mail-send-line"></i>
                  Reserve My Seat
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom note */}
        <p className="text-center text-black/40 text-xs mt-8 tracking-wide">
          The Full 90 deal ($30 unlimited beers &amp; mimosas) is available at every seat during any game. Zone upgrades are $20/person and require minimum guest count to be met.
        </p>
      </div>
    </section>
  );
}
